import { cx } from '@emotion/css';
import { Col } from 'antd';
import { isUndefined } from 'lodash';
import { useSelector } from 'react-redux';
import { OrderTypeDto } from 'src/dtos';
import { useSubmittedAccountSelector } from 'src/hooks';
import {
  CurrencyField,
  MButton,
  MFormDateField,
  MFormTextField,
  MModalBody,
  MModalFooter,
  MModalTitle,
  MTooltip,
  MTypography,
  SubmissionResult,
} from 'src/lib';
import { mapOrderDurationDtoToLabel, mapOrderTypeDtoToLabel } from 'src/mappers';
import { Quote, SecurityOrderView } from 'src/models';
import { BaseStyle, Spacing } from 'src/styles';
import { getInvestorName } from 'src/utils';

interface SecurityHoldingTradeFailedStepProps {
  quote: Quote;
  quantity?: number;
  order: SecurityOrderView;
  onLastPrice: () => void;
  onServiceFee: () => void;
  onClose: () => void;
}

export const SecurityHoldingTradeFailedStep = ({
  quote,
  quantity,
  order,
  onLastPrice,
  onServiceFee,
  onClose,
}: SecurityHoldingTradeFailedStepProps) => {
  const error = useSelector((state: any) => state.orders.create.error);
  const { account } = useSubmittedAccountSelector();

  const physicalAddress = [
    account.primaryAccountHolder.physicalAddress.address1,
    account.primaryAccountHolder.physicalAddress.address2,
    account.primaryAccountHolder.physicalAddress.city,
    account.primaryAccountHolder.physicalAddress.postalCode,
    account.primaryAccountHolder.physicalAddress.country.value,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <>
      <MModalTitle>Security Order Failed</MModalTitle>
      <MModalBody>
        <Col span={24} className={cx(BaseStyle.level, Spacing.my12)}>
          <SubmissionResult type='error' message={error?.message} testId={'failed'} />
        </Col>
        <Col span={24} className={cx(BaseStyle.level, Spacing.my24)} data-testid={'security-order-failed-symbol'}>
          <MTypography variant='h6' align='center'>
            {quote?.symbol}
          </MTypography>
        </Col>
        <Col span={24}>
          <MFormTextField
            label='Investor Name'
            value={getInvestorName(account)}
            testId='security-order-failed-investor-name'
          />
        </Col>
        <Col span={24}>
          <MFormTextField label='Address' value={physicalAddress} testId='security-order-failed-address' />
        </Col>
        <Col span={24}>
          <MFormDateField label='Date' value={new Date().toISOString()} testId='security-order-failed-date' />
        </Col>
        <Col span={24}>
          <MFormTextField
            label='Order Type'
            value={order.type ? mapOrderTypeDtoToLabel(order.type) : undefined}
            testId='security-order-failed-order-type'
          />
        </Col>
        <Col span={24}>
          <MFormTextField
            label={
              <>
                Last Price
                <MTooltip showIcon onClick={() => onLastPrice()} />
              </>
            }
            value={<CurrencyField value={quote.last} />}
            testId='security-order-failed-last-price'
          />
        </Col>
        {!isUndefined(quantity) && (
          <Col span={24}>
            <MFormTextField label='Shares Held' value={quantity} testId='security-order-failed-shares-held' />
          </Col>
        )}
        {order.type === OrderTypeDto.Limit || order.type === OrderTypeDto.StopLimit ? (
          <Col span={24}>
            <MFormTextField
              label='Limit Price'
              value={<CurrencyField value={order.price} />}
              testId='security-order-failed-limit-price'
            />
          </Col>
        ) : null}
        {order.type === OrderTypeDto.StopLimit || order.type === OrderTypeDto.StopMarket ? (
          <Col span={24}>
            <MFormTextField
              label='Stop Price'
              value={<CurrencyField value={order.stop} />}
              testId='security-order-failed-stop-price'
            />
          </Col>
        ) : null}
        <Col span={24}>
          <MFormTextField
            label={`Shares to ${order.action}`}
            value={order.quantity}
            testId='security-order-failed-shares-to-action'
          />
        </Col>
        <Col span={24}>
          <MFormTextField
            label='Order Expiration'
            value={order.duration ? mapOrderDurationDtoToLabel(order.duration) : undefined}
            testId='security-order-failed-order-expiration'
          />
        </Col>
        <Col span={24}>
          <MFormTextField
            label={
              <>
                Service Fee
                <MTooltip showIcon onClick={() => onServiceFee()} />
              </>
            }
            value='$10.00'
            testId='security-order-failed-service-fee'
          />
        </Col>
        <Col span={24}>
          <MFormTextField
            label='Estimated Total'
            value={<CurrencyField value={order.estimatedTotal} />}
            testId='security-order-failed-estimated-total'
          />
        </Col>
        <Col span={24}></Col>
      </MModalBody>
      <MModalFooter className={BaseStyle.levelRight}>
        <MButton type='tertiary' onClick={onClose}>
          Close
        </MButton>
      </MModalFooter>
    </>
  );
};
